import { FC } from 'react'
import { Box, IconButton, SelectChangeEvent, Typography } from '@mui/material'
import { EyeIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import theme from 'theme'
import { ThemeButton } from 'ui/components/buttons'
import SelectV2 from 'ui/components/form-components-v2/select-v2'
import SelectWithPopoverMenuV2 from 'ui/components/form-components-v2/select-with-popover-menu-v2'
import { JiraFilterFieldCatalogItem, JiraFilterOperator, TreeSelectOption } from 'models'
import { StepBody } from '../../jiraWizard.styles'
import { FilterActionsRow, FilterRowsWrapper, fieldSelectSx } from '../data-configuration/dataConfiguration.styles'
import {
  FilterDraftRow,
  emptyFilterRow,
  resolveOptionValues,
  toOption
} from '../data-configuration/dataConfiguration.utils'
import { JiraOptionSource } from '../data-configuration/useJiraOptions'

/* The endpoint reports the missing scope in a machine-readable warning; the user
 * gets this instead. */
const MISSING_OPTIONS_HINT =
  'Options are unavailable — the connected Jira token is missing the read:jira-user scope. Ask your Jira admin to grant it, or filter on another field.'

const NO_FILTERS_HINT = 'No filters — every issue from the connected projects will be ingested.'

const PLACEHOLDER = 'Select'

/* Joining every label is unreadable once the field truncates it — and a field
 * can be offered hundreds of values — so past a couple the field reports a count. */
const summariseValues = (selected: TreeSelectOption[], total: number) => {
  if (!selected.length) return PLACEHOLDER
  if (total && selected.length === total) return `All values (${total})`
  if (selected.length > 2) return `${selected.length} selected`

  return selected.map((option) => option.label).join(', ')
}

interface Props {
  rows: FilterDraftRow[]
  onRowsChange: (rows: FilterDraftRow[]) => void
  fields: JiraFilterFieldCatalogItem[]
  operators: JiraFilterOperator[]
  optionsFor: (jiraField: string) => JiraOptionSource
  loadingOptions?: boolean
  disabled?: boolean
  previewing?: boolean
  previewDisabled?: boolean
  onPreview: () => void
}

/* Data Filtering step: rows of field + operator + values over the filterable
 * fields of the fields catalog. Advancing with no rows is valid and means no
 * restriction beyond the connected project. */
const DataFilteringStep: FC<Props> = ({
  rows,
  onRowsChange,
  fields,
  operators,
  optionsFor,
  loadingOptions,
  disabled,
  previewing,
  previewDisabled,
  onPreview
}) => {
  const fieldOptions = fields
    .filter((field) => field.is_for_data_filter)
    .map((field) => ({ label: field.display_name, value: field.jira_field }))
  const operatorOptions = operators.map((operator) => ({ label: operator.display_name, value: operator.name }))

  /* Fall back to the raw key: a saved row must show what it holds even if the
   * catalog has not loaded, or no longer carries that field. */
  const labelFor = (options: { label: string; value: string }[], value: string) =>
    options.find((option) => option.value === value)?.label || value

  const updateRow = (uid: number, patch: Partial<FilterDraftRow>) =>
    onRowsChange(rows.map((row) => (row.uid === uid ? { ...row, ...patch } : row)))

  const handleFieldChange = (row: FilterDraftRow, field: string) =>
    updateRow(row.uid, { field, values: [], operator: row.operator || operators[0]?.name || '' })

  return (
    <StepBody>
      <FilterRowsWrapper>
        {rows.map((row) => {
          const source = optionsFor(row.field)
          const valuesDisabled = disabled || !row.field || !!source.warning

          return (
            <Box className="filter-row" key={row.uid}>
              <Box className="filter-row-field">
                <SelectV2
                  id={`filter-field-${row.uid}`}
                  label=""
                  searchable
                  value={row.field}
                  options={fieldOptions}
                  disabled={disabled}
                  displayEmpty
                  renderValue={() => labelFor(fieldOptions, row.field) || PLACEHOLDER}
                  sx={fieldSelectSx(!!row.field)}
                  onChange={(e: SelectChangeEvent<unknown>) => handleFieldChange(row, e.target.value as string)}
                />
              </Box>

              <Box className="filter-row-field">
                <SelectV2
                  id={`filter-operator-${row.uid}`}
                  label=""
                  value={row.operator}
                  options={operatorOptions}
                  disabled={disabled || !row.field}
                  displayEmpty
                  renderValue={() => labelFor(operatorOptions, row.operator) || PLACEHOLDER}
                  sx={fieldSelectSx(!!row.operator)}
                  onChange={(e: SelectChangeEvent<unknown>) =>
                    updateRow(row.uid, { operator: e.target.value as string })
                  }
                />
              </Box>

              <Box className="filter-row-field">
                <SelectWithPopoverMenuV2
                  label=""
                  labelId={`filter-values-${row.uid}`}
                  searchable
                  displayEmpty
                  placeholder={PLACEHOLDER}
                  selectAllCheckboxLabel="All values"
                  options={source.values.map(toOption)}
                  value={row.values.map(toOption)}
                  isLoading={loadingOptions}
                  disabled={valuesDisabled}
                  sx={fieldSelectSx(!!row.values.length)}
                  renderSelected={(selected) => summariseValues(selected, source.values.length)}
                  onChange={(selected) =>
                    updateRow(row.uid, {
                      values: resolveOptionValues(
                        selected.map((option) => option.value as string),
                        source.values,
                        row.values
                      )
                    })
                  }
                />
                {!!source.warning && <Typography className="section-hint">{MISSING_OPTIONS_HINT}</Typography>}
              </Box>

              <IconButton
                className="delete-button"
                disabled={disabled}
                onClick={() => onRowsChange(rows.filter((r) => r.uid !== row.uid))}
              >
                <TrashIcon width={theme.iconSize.xxSmall} height={theme.iconSize.xxSmall} />
              </IconButton>
            </Box>
          )
        })}

        {!rows.length && <Typography className="section-hint">{NO_FILTERS_HINT}</Typography>}
      </FilterRowsWrapper>

      <FilterActionsRow>
        <ThemeButton
          className="add-filter-button"
          themevariant="secondary"
          shape="rectangular"
          disabled={disabled}
          onClick={() => onRowsChange([...rows, emptyFilterRow()])}
          startIcon={<PlusIcon width={16} height={16} />}
          sx={{ '& svg path': { fill: 'none !important' } }}
        >
          Add Filter
        </ThemeButton>

        {/* Previewing runs against the drafted rows — it needs no step submit. */}
        <ThemeButton
          themevariant="hollow"
          disabled={previewDisabled}
          onClick={onPreview}
          startIcon={<EyeIcon width={16} height={16} />}
          sx={{ '& svg path': { fill: 'none !important' } }}
        >
          {previewing ? 'Loading preview...' : 'Preview Data'}
        </ThemeButton>
      </FilterActionsRow>
    </StepBody>
  )
}

export default DataFilteringStep
